// src/pages/StudentDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  FaUserGraduate, FaPhone, FaMapMarkerAlt, FaChalkboardTeacher,
  FaEnvelope, FaTint, FaUserTie, FaUniversity, FaIdCard, FaUsers, FaHome, FaVenusMars, FaLandmark
} from "react-icons/fa";
import "../StudentDetails.css";
import api from "../../api"; // ensure this exists

const safe = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const StudentDetails = () => {
  const { encodedId } = useParams();
  const [student, setStudent] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  useEffect(() => {
    let mounted = true;
    const fetchStudent = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/view-student-details/${encodedId}`);
        const payload = res?.data || {};

        // Determine student raw record from possible shapes
        let s = null;
        if (payload.student) {
          s = Array.isArray(payload.student) ? (payload.student[0] || null) : payload.student;
        } else if (payload.data && payload.data.student) {
          s = Array.isArray(payload.data.student) ? (payload.data.student[0] || null) : payload.data.student;
        } else if (payload.data && payload.data.length) {
          // fallback if API returns array directly
          s = payload.data[0];
        } else if (payload.length) {
          s = payload[0];
        }

        if (!s) {
          throw new Error(payload.message || "Student not found");
        }

        // Normalize roll numbers: support array or comma-separated string or missing
        let roll_no = "";
        if (Array.isArray(s.roll_numbers) && s.roll_numbers.length) {
          roll_no = s.roll_numbers[0] || "";
        } else if (typeof s.roll_numbers === "string" && s.roll_numbers.length) {
          const parts = s.roll_numbers.split(",");
          roll_no = parts.length ? parts[0].trim() : "";
        } else if (s.roll_no) {
          roll_no = s.roll_no;
        } else if (s.student_roll_no) {
          roll_no = s.student_roll_no;
        }

        // Normalize bank fields (different possible names)
        const bank_name = s.bank_name || s.bank || (s.bank && s.bank.bank_name) || "";
        const account_no = s.account_number || s.account_no || (s.bank && s.bank.account_number) || "";
        const ifsc_code = s.ifsc || s.ifsc_code || (s.bank && s.bank.ifsc) || "";

        // normalize address fields: could exist directly or nested under address
        const address = s.address || {};
        const locality = s.locality || address.locality || "";
        const village = s.village || address.village || "";
        const post_office = s.post_office || address.post_office || "";
        const police_station = s.police_station || address.police_station || "";
        const block = s.block || s.block_name || address.block || "";
        const district = s.district || s.district_name || address.district || "";
        const state = s.state || s.state_name || address.state || "";
        const pincode = s.pincode || address.pincode || "";

        const normalized = {
          id: s.id ?? s.student_id ?? null,
          student_code: s.student_code || s.code || "",
          name: s.name || s.full_name || "",
          mobile: s.mobile || s.phone || "",
          email: s.email || "",
          class_name: s.class_name || s.class || "",
          section_name: s.section_name || s.section || "",
          stream_name: s.stream_name || "",
          gender: s.gender || "",
          category: s.category || "",
          blood_group: s.blood_group || "",
          father_name: s.father_name || s.father || "",
          father_aadhaar_no: s.father_aadhaar_no || s.father_aadhaar || "",
          mother_name: s.mother_name || s.mother || "",
          mother_aadhaar_no: s.mother_aadhaar_no || s.mother_aadhaar || "",
          locality,
          village,
          post_office,
          police_station,
          block,
          district,
          state,
          pincode,
          bank: bank_name,
          account_no,
          ifsc_code,
          file: s.file || s.photo || s.avatar || "",
          roll_no,
          _raw: s
        };

        if (mounted) {
          setStudent(normalized);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          console.error("Student fetch error:", err?.response || err);
          setError(err?.response?.data?.message || err?.message || "Something went wrong");
          setLoading(false);
        }
      }
    };

    fetchStudent();
    return () => { mounted = false; };
  }, [encodedId]);

  if (loading) return <p style={{ padding: 24 }}>Loading student details…</p>;
  if (error) return <p style={{ color: "red", padding: 24 }}>{error}</p>;
  if (!student) return <p style={{ padding: 24 }}>No student data found.</p>;

  return (
    <div>
      <div className="student-container" style={{ marginTop: 0 }}>
        <div className="layout-container" style={{ display: "flex" }}>
          <div className="content" style={{ flex: 1, padding: 20 }}>
            <h2 className="title">Student Details</h2>

            <div className="student-card" style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
              <div style={{ width: 180 }}>
                <img
                  src={student.file || "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"}
                  alt="Student"
                  style={{ width: 160, height: 160, borderRadius: 8, objectFit: "cover" }}
                />
                <div style={{ marginTop: 8, textAlign: "center", fontWeight: 700 }}>{safe(student.name)}</div>
                <div style={{ textAlign: "center", color: "#666" }}>{safe(student.student_code)}</div>
              </div>

              <div className="student-info" style={{ flex: 1 }}>
                {/* Personal Details */}
                <div className="details-section" style={{ marginBottom: 14 }}>
                  <h3>Personal Details</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <Row label="Name" icon={<FaUserGraduate />} value={student.name} />
                    <Row label="Student ID" icon={<FaIdCard />} value={student.student_code} />
                    <Row label="Blood Group" icon={<FaTint />} value={student.blood_group} />
                    <Row label="Class" icon={<FaChalkboardTeacher />} value={student.class_name} />
                    <Row label="Section" icon={<FaChalkboardTeacher />} value={student.section_name} />
                    <Row label="Stream" icon={<FaChalkboardTeacher />} value={student.stream_name} />
                    <Row label="Gender" icon={<FaVenusMars />} value={student.gender} />
                    <Row label="Category" icon={<FaUsers />} value={student.category} />
                    <Row label="Roll No" icon={<FaUserGraduate />} value={student.roll_no} />
                  </div>
                </div>

                {/* Contact Details */}
                <div className="details-section" style={{ marginBottom: 14 }}>
                  <h3>Contact Details</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <Row label="Mobile" icon={<FaPhone />} value={student.mobile} />
                    <Row label="Email" icon={<FaEnvelope />} value={student.email} />
                  </div>
                </div>

                {/* Parent Details */}
                <div className="details-section" style={{ marginBottom: 14 }}>
                  <h3>Parent Details</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <Row label="Father's Name" icon={<FaUserTie />} value={student.father_name} />
                    <Row label="Father Aadhaar" icon={<FaIdCard />} value={student.father_aadhaar_no} />
                    <Row label="Mother's Name" icon={<FaUserTie />} value={student.mother_name} />
                    <Row label="Mother Aadhaar" icon={<FaIdCard />} value={student.mother_aadhaar_no} />
                  </div>
                </div>

                {/* Address */}
                <div className="details-section" style={{ marginBottom: 14 }}>
                  <h3>Address Details</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <Row label="Locality" icon={<FaLandmark />} value={student.locality} />
                    <Row label="Village" icon={<FaHome />} value={student.village} />
                    <Row label="Post Office" icon={<FaMapMarkerAlt />} value={student.post_office} />
                    <Row label="Police Station" icon={<FaMapMarkerAlt />} value={student.police_station} />
                    <Row label="Block" icon={<FaMapMarkerAlt />} value={student.block} />
                    <Row label="District" icon={<FaMapMarkerAlt />} value={student.district} />
                    <Row label="State" icon={<FaMapMarkerAlt />} value={student.state} />
                    <Row label="Pin Code" icon={<FaMapMarkerAlt />} value={student.pincode} />
                  </div>
                </div>

                {/* Bank */}
                <div className="details-section" style={{ marginBottom: 14 }}>
                  <h3>Bank Details</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <Row label="Bank Name" icon={<FaUniversity />} value={student.bank} />
                    <Row label="Account No" icon={<FaUniversity />} value={student.account_no} />
                    <Row label="IFSC Code" icon={<FaUniversity />} value={student.ifsc_code} />
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// small Row component for label + icon + value
function Row({ label, icon, value }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", background: "#fff", borderRadius: 6 }}>
      <div style={{ width: 28, textAlign: "center", color: "#1976d2" }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: "#666", fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 14, color: "#222", fontWeight: 700 }}>{safe(value)}</div>
      </div>
    </div>
  );
}

export default StudentDetails;
